"use client"

import { usePathname } from "next/navigation"
import { Fragment, useEffect, useState } from "react"
import styled, { css } from "styled-components"

import { NestedNavigationLinksType } from "common/SiteLinks"
import { trackMenuClicks } from "common/analytics"
import NextLink from "common/bit/NextLink"
import useMounted from "common/useMounted"
import { closeCollapse, openCollapse } from "layout/oldLayout/CollapseLayout"
import ButtonBaseBlank from "reusable/ButtonBaseBlank"
import { useAppDispatch } from "store"
import { StyledRow } from "theme/shared"
import ShopAuthLink, { StyledTypographyCenter } from "./ShopAuthLink"
import SignOutDialog from "./SignOutDialog"

// Shared nav-item styling (DLK-553): white text at 0.85 opacity; the active item
// is bold, full opacity, with a 1px red underline (Figma node 15530:186652).
const navItemStyles = css<{ $active?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  p {
    color: ${(p) => p.theme.colors.white};
    opacity: ${(p) => (p.$active ? 1 : 0.85)};
    font-weight: ${(p) => (p.$active ? 700 : 400)};
    letter-spacing: 0.8px;
    white-space: nowrap;
    transition: opacity 0.2s ease;
  }
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background-color: ${(p) => p.theme.colors.newPrimaryRed};
    opacity: ${(p) => (p.$active ? 1 : 0)};
    transition: opacity 0.2s ease;
  }
  &:hover p {
    opacity: 1;
    text-decoration: none;
  }
`

const NavLinkSpan = styled.span<{ $active?: boolean }>`
  ${navItemStyles}
`

const NavLinkButton = styled(ButtonBaseBlank)<{ $active?: boolean }>`
  ${navItemStyles}
`

const StyledTypographyCenterHeader = styled(StyledTypographyCenter)`
  ${(p) => p.theme.breakpoints.down("smallLaptop")} {
    font-size: 16px;
    line-height: 140%;
  }
`

const StyledRowHeader = styled(StyledRow)`
  ${(p) => p.theme.breakpoints.down("smallLaptop")} {
    gap: 10px 16px;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 3px 0;
  }
`

type HeaderLinkListProps = {
  linkList: NestedNavigationLinksType[]
  setSubLinks: (val: any) => void
  isShopHeader: boolean
  closeDrawer?: () => void
}

export default function HeaderLinkList({
  linkList,
  setSubLinks,
  isShopHeader = false,
}: HeaderLinkListProps) {
  const [selectedLink, setSelectedLink] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const dispatch = useAppDispatch()
  const pathname = usePathname()
  const mounted = useMounted()

  // 🔄 reset state on route change
  useEffect(() => {
    setSubLinks(null)
    setSelectedLink(null)
    dispatch(closeCollapse())
  }, [pathname]) // runs whenever URL path changes

  // Active = clicked-open dropdown, or a link whose path matches the route.
  const matchesPath = (link: string) => {
    if (!link || !mounted) return false
    try {
      const url = new URL(link, window.location.origin)
      // CMS links can be absolute. An absolute link to a different host (e.g. the
      // blog "https://blog.mazda.co.il") is external and never the active route —
      // otherwise its "/" path falsely matched the homepage (DLK-749). Relative
      // links resolve against the current origin. Gated on `mounted` so window is
      // only read on the client (SSR-safe, no hydration mismatch).
      if (url.hostname !== window.location.hostname) return false
      return url.pathname === pathname
    } catch {
      return link === pathname
    }
  }
  const isActive = (item: NestedNavigationLinksType) => {
    if (item.title === selectedLink) return true
    if (item.links.length === 1) return matchesPath(item.links[0].link)
    return item.links.some((sub) => matchesPath(sub.link))
  }

  return (
    <StyledRowHeader
      $gap={isShopHeader ? "10px" : "12px 24px"}
      $notCenter
      $fullWidth
      alignItems="center"
      $wrap
    >
      <SignOutDialog dialogOpen={dialogOpen} setDialogOpen={setDialogOpen} />

      {linkList.map((item, index) => (
        <Fragment key={index}>
          {item.links.length === 1 ? (
            <NextLink
              href={item.links[0].link}
              openInNewTab={item.links[0].blank}
            >
              <NavLinkSpan
                $active={isActive(item)}
                onClick={() => {
                  setSubLinks(null)
                  trackMenuClicks()
                }}
              >
                <StyledTypographyCenterHeader variant="body1">
                  {item.title}
                </StyledTypographyCenterHeader>
              </NavLinkSpan>
            </NextLink>
          ) : (
            <>
              {!item.links.length && isShopHeader ? (
                <ShopAuthLink
                  item={item}
                  selectedLink={selectedLink}
                  setDialogOpen={setDialogOpen}
                />
              ) : (
                <NavLinkButton
                  $active={isActive(item)}
                  onClick={() => {
                    if (item.title === selectedLink) {
                      setSubLinks(null)
                      setSelectedLink(null)
                      dispatch(closeCollapse())
                    } else {
                      setSubLinks(item.links)
                      setSelectedLink(item.title)
                      dispatch(openCollapse())
                    }
                    trackMenuClicks()
                  }}
                  aria-expanded={item.title === selectedLink}
                >
                  <StyledTypographyCenterHeader variant="body1">
                    {item.title}
                  </StyledTypographyCenterHeader>
                </NavLinkButton>
              )}
            </>
          )}
        </Fragment>
      ))}
    </StyledRowHeader>
  )
}
